import { Content, FormatContent } from 'src/contexts/ContentContext';
import {
  FailureMessage,
  SuccessMessage,
  WarningMessage,
  WorkingMessage,
} from 'src/core/POS/MessageWithIcon';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { BulkEditProgress, BulkEditStep } from 'src/WebApiController';

import { ActionOutboxEntityTypeForBulkEdit } from '../../bulkEditUtils';
import * as styles from './BulkEditProgressDisplay.css';
import { BulkEditStepMessage } from './BulkEditStepMessage';
import { CountsByEntityDisplay } from './CountsByEntityDisplay';
import { GenericProgressDisplay } from './GenericProgressDisplay';
import { hasCountsByEntity } from './utils';

export type BulkEditProgressDisplayProps = BulkEditProgress & {
  step: BulkEditStep;
  entityType?: ActionOutboxEntityTypeForBulkEdit;
};

export function BulkEditProgressDisplay({
  countsByEntity,
  successPercent,
  failurePercent,
  warningPercent,
  successCount,
  failureCount,
  warningCount,
  totalCount,
  status,
  step,
  entityType,
  retryableEntities,
}: BulkEditProgressDisplayProps) {
  const skippedItemCount = entityType
    ? retryableEntities?.bulkEditCountPreview[entityType]?.idsAffected.length
    : undefined;

  return (
    <Stack direction="column" gap="l">
      <Stack className={styles.root} direction="column">
        <Stack direction="row" gap="l" justifyContent="spaceBetween">
          <BulkEditStepMessage step={step} />
          <Stack direction="column" alignItems="end">
            {hasCountsByEntity(countsByEntity) ? (
              <CountsByEntityDisplay countsByEntity={countsByEntity} />
            ) : (
              <GenericProgressDisplay
                totalCount={totalCount}
                warningCount={warningCount}
                warningPercent={warningPercent ?? 0}
                failureCount={failureCount}
                failurePercent={failurePercent ?? 0}
                successCount={successCount}
                successPercent={successPercent ?? 0}
              />
            )}
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="spaceBetween">
          {step === BulkEditStep.Done ||
          step === BulkEditStep.DoneWithRetryableEntities ? (
            <>
              {!totalCount && (
                <WarningMessage
                  message={<Content id={ContentId.NoItemsWereChanged} />}
                />
              )}
              {!!failureCount && (!!warningCount || !!successCount) && (
                <WarningMessage
                  message={<Content id={ContentId.BulkEditPartialFailure} />}
                />
              )}
              {!failureCount && !!warningCount && !!successCount && (
                <SuccessMessage
                  message={
                    <Content id={ContentId.BulkEditSuccessWithWarnings} />
                  }
                />
              )}
              {!failureCount && !warningCount && !!successCount && (
                <SuccessMessage
                  message={<Content id={ContentId.BulkEditSuccess} />}
                />
              )}
              {!!failureCount && !warningCount && !successCount && (
                <FailureMessage
                  message={<Content id={ContentId.BulkEditFailed} />}
                />
              )}
            </>
          ) : (
            <WorkingMessage message={status ?? ''} />
          )}
        </Stack>
      </Stack>

      {retryableEntities && entityType && (
        <WarningMessage
          message={
            <FormatContent
              id={FormatContentId.BulkEditSkippedItemsPrompt}
              params={[skippedItemCount?.toString() ?? '']}
            />
          }
          messageStyle={{ fontSize: vars.typography.fontSize.lg }}
        />
      )}
    </Stack>
  );
}
